import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Drawer,
    Radio,
    RadioGroup,
    FormControlLabel,
    IconButton,
    Checkbox,
    TableContainer
} from "@mui/material";
import { Close, ArrowBack } from '@mui/icons-material';
import { useState, useContext } from "react";
import { Bold } from "lucide-react";
import { ReactComponent as UploadIcon } from "../../../images/uploadIcon.svg";
import { ReactComponent as WhiteUploadIcon } from "../../../images/WhiteUploadIcon.svg";
import { ReactComponent as NoDataIcon } from "../../../images/NoDataCapturedIcon.svg";
import { SearchIcon } from "lucide-react";
import { ReactComponent as DeleteIcon } from "../../../images/APIDeleteNewIcon.svg";
import { color } from "echarts";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import yaml from "js-yaml";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { useNavigate, useLocation } from "react-router-dom";
import { generateTestCasesForAPI, swaggerToPostman, APITraceabilityMatrix, endpoitnExtractionAPI } from "../../../services/webServices";
import MessagePopup from "../../popup-components/MessagePopup";
import { ReactComponent as UploadFileIcon } from "../../../images/uploadedFileIcon.svg";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI.js";
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { resourceChainTestCases } from "../../../services/webServices";





function UploadAPISpecs() {


    const navigate = useNavigate();
    const location = useLocation();
    const { setLoadingState } = useContext(LoadingModalContext);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [fileContentItems, setFileContentItems] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [fileContentEndpoints, setFileContentEndpoints] = useState([]);
    const [search, setSearch] = useState("");
    const [traceabilityMatrixData, setTraceabilityMatrixData, endpointExtraction] = useState({});
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectionMode, setSelectionMode] = useState('choose');
    const [selectedTestCases, setSelectedTestCases] = useState([]);


    const extractEndpoints = (openApiSpec) => {
        if (!openApiSpec.paths) return [];

        const endpoints = [];

        Object.entries(openApiSpec.paths).forEach(([path, methods]) => {
            Object.entries(methods).forEach(([method, details]) => {
                endpoints.push({
                    method: method.toUpperCase(),
                    endpoint: path,
                    params: details.parameters
                        ? details.parameters.map((p) => p.name).join(", ")
                        : "-",
                    headers: details.responses?.["200"]?.content
                        ? Object.keys(details.responses["200"].content).join("/n")
                        : "-",
                    authType: details.security
                        ? details.security.flatMap(sec => Object.keys(sec)).join(", ")
                        : "-",
                    body: details.requestBody?.content
                        ? Object.keys(details.requestBody.content).join(", ")
                        : "-"



                });
            });
        });

        return endpoints;
    };


    const handleFileRemove = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setUploadedFile(null);
        setFileContent(null);
        setFileContentItems([]);
        setFileContentEndpoints([]);
    };


    function flattenPostmanItems(items) {
        const flat = [];

        function recurse(subItems) {
            subItems.forEach(item => {
                if (item.request) {
                    flat.push(item); // it's a request
                } else if (item.item && item.item.length > 0) {
                    recurse(item.item); // it's a folder
                }
            });
        }
        recurse(items);
        return flat;
    }

    const filteredRows = fileContentEndpoints?.filter((row) =>

        Object.values(row).some((val) =>
            (val?.toString() ?? '').toLowerCase().includes(search.toLowerCase())
        )
    );

    const functionalTestCases = [
        { id: 'Positive test case', label: 'Positive test cases' },
        { id: 'Negative test case', label: 'Negative test cases' },
        { id: 'Error Handling test case', label: 'Error handling test cases' },
        { id: 'Boundary test case', label: 'Boundary test cases' },
        { id: 'Resource Chain test case', label: 'End-End testing(Resource-Chain)' },
    ];

    const nonFunctionalTestCases = [
        { id: 'Performance test case', label: 'Performance test cases' },
        { id: 'Security test case', label: 'Security test cases' },

    ];

    const handleTestCaseToggle = (testCaseId) => {
        setSelectedTestCases(prev =>
            prev.includes(testCaseId)
                ? prev.filter(id => id !== testCaseId)
                : [...prev, testCaseId]
        );
    };

    const handleSelectionModeChange = (event) => {
        setSelectionMode(event.target.value);
        if (event.target.value === 'all') {
            setSelectedTestCases([]);
        }
    };



    const handleBack = () => {
        setSelectedTestCases([]);
        setSelectionMode('choose');
        setDrawerOpen(false)
    };


    const renderInputData = (inputData) => {
        if (!inputData || Object.keys(inputData).length === 0) {
            return "-";
        }
        if (typeof inputData === 'object') {
            return JSON.stringify(inputData, null, 2);
        }
        return inputData;
    };


    const handleFileUpload = async (event, droppedFile = null) => {
        let progressHandler;
        const file = droppedFile || event.target.files[0];


        if (!file) {
            setPopupMessage("No file selected or unsupported file type.");
            setPopupOpen(true);
            return;
        }

        const fileName = file.name;
        const validExtensions = [".json", ".yaml", ".yml", ".postman_collection"];
        const fileExtension = fileName
            .slice(fileName.lastIndexOf("."))
            .toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            setPopupMessage("Please upload a valid file (.json, .yaml, .yml).");
            setPopupOpen(true);
            return;
        }

        setUploadedFile(file);
        const reader = new FileReader();

        reader.onload = async (event) => {
            let forEndpoints = null;
            let yamlendpoints = null;
            try {
                const content = event.target.result;
                let parsedYAML;
                if (fileExtension === ".json" || fileExtension === ".postman_collection") {
                    parsedYAML = JSON.parse(content);

                } else {
                    parsedYAML = yaml.load(event.target.result);
                    yamlendpoints = parsedYAML;

                }
                if (parsedYAML.item === undefined && fileExtension === ".json") {
                    // alert(
                    //   "Upload a valid input file.")
                    setPopupMessage("Upload a valid input file.");
                    setPopupOpen(true);
                    setUploadedFile(null)

                }
                if (parsedYAML && parsedYAML.item && parsedYAML.item.length > 0 && (fileExtension === ".json" || fileExtension === ".postman_collection")) {
                    forEndpoints = parsedYAML.item;
                    setFileContentItems(parsedYAML.item);


                }
                else if (parsedYAML && Object.keys(parsedYAML).length > 0 && (fileExtension !== ".json" && fileExtension !== ".postman_collection")) {
                    yamlendpoints = parsedYAML
                    setFileContent(parsedYAML);

                    // const endpoints = extractEndpoints(parsedYAML);
                    // setFileContentEndpoints(endpoints);


                }

                else {
                    setFileContent(null);
                }


                progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 14000 });
                progressHandler.start("Extracting Endpoints");
                if (forEndpoints && forEndpoints.length > 0) {
                    try {
                        //Mew code using promise for parallel calls
                        const promise = forEndpoints.map((item) => {
                            return endpoitnExtractionAPI(item)
                        })

                        const results = await Promise.allSettled(promise);
                        const allTestCases = [];
                        results.forEach((result) => {
                            if (result.status === "fulfilled" && result.value?.data) {
                                allTestCases.push(...result.value.data);
                            } else {
                                console.error("One call failed:", result.reason);
                            }
                        })

                        if (allTestCases && allTestCases.length > 0) {


                            setFileContentEndpoints(allTestCases)

                        }
                    } catch (error) {
                        console.error(error)
                    }
                }
                if (yamlendpoints !== null) {
                    const responseData = await swaggerToPostman(yamlendpoints);

                    if (responseData.data.item) {
                        const flattenedItems = flattenPostmanItems(responseData.data.item);

                        // const batchSize = 3;
                        // const batches = [];
                        const endpointsyaml = [];

                        // Create batches
                        // for (let i = 0; i < flattenedItems.length; i += batchSize) {
                        //     batches.push(flattenedItems.slice(i, i + batchSize));
                        // }

                        const promises = flattenedItems.map(async (batch, batchIndex) => {
                            try {
                                // Fixed typo: endpoitnExtractionAPI -> endpointExtractionAPI
                                const testcasesresponse = await endpoitnExtractionAPI({ item: batch });

                                if (testcasesresponse.data && testcasesresponse.data) {

                                    // Return the actual data instead of empty array
                                    return testcasesresponse.data;
                                }

                            } catch (error) {
                                console.error(
                                    error?.response?.data?.error || error.message,
                                    "Error processing batch:", batchIndex
                                );
                            }
                            return []; // Return empty array if no data or error
                        });

                        const results = await Promise.allSettled(promises);

                        // Fixed: Access the fulfilled value correctly
                        results.forEach((result) => {
                            if (result.status === "fulfilled" && result.value) {
                                // If result.value is an array, spread it; if it's an object, push it
                                if (Array.isArray(result.value)) {
                                    endpointsyaml.push(...result.value);
                                } else {
                                    endpointsyaml.push(result.value);
                                }
                            } else if (result.status === "rejected") {
                                console.error("One call failed:", result.reason);
                            }
                        });

                        if (endpointsyaml && endpointsyaml.length > 0) {

                            setFileContentEndpoints(endpointsyaml);
                        }
                    }
                }

                await progressHandler.finishAndClose();



            } catch (error) {
                console.error("Error parsing YAML:", error);
                //await progressHandler.finishAndClose();
                setFileContent(null);
                setPopupMessage(
                    "Error parsing file. Please upload a valid YAML or JSON."
                );
                setPopupOpen(true);
                setUploadedFile(null)
            }
            finally {
                await progressHandler.finishAndClose();
            }
        };
        reader.readAsText(file);
    };


    const handleShowCatergory = () => {
        setDrawerOpen(true);
    }


    const generateTestCases = async () => {


        const testCasesToGenerate = selectionMode === 'all'
            ? [
                "Positive test case", "Negative test case", "Boundary test case", "Error Handling test case",
                "Security test case", "Performance test case", "Resource Chain test case"]
            : selectedTestCases;



        const totalFolders = fileContentItems.length;

        let progressHandler;
        progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 216000 });
        progressHandler.start("Generating test cases from API endpoints");


        let tcCounter = 1; // global test case ID counter
        const finalArray = [];
        let traceabilityMatrix = null;





        try {

            const isOnlyResourceChain =
                selectedTestCases.length === 1 &&
                selectedTestCases.includes("Resource Chain test case");

            if (isOnlyResourceChain) {
                // Only call resourceChainTestCases API and skip all others
                progressHandler.updateMessage("Generating Resource Chain Test Cases");

                const resourceChainResponse = await resourceChainTestCases({
                    endpoints: fileContentItems.length > 0 ? fileContentItems : fileContent,
                });

                if (resourceChainResponse?.data?.length > 0) {
                    finalArray.push(...resourceChainResponse.data);
                } else {
                    console.warn(" No Resource Chain Test Cases generated");
                }

                await progressHandler.finishAndClose();

                // Navigate to display page with only resource chain test cases
                if (finalArray.length > 0) {
                    navigate("/dashboard/api-testing/test-cases-display", {
                        state: {
                            ...location.state,
                            generateTestCasesData: finalArray,
                            button: "importAPIDefinition",
                            testCasesToGenerate: testCasesToGenerate,
                        },
                    });
                }
                return; 
            }

            if (fileContentItems && fileContentItems.length > 0) {

                try {



                    //Mew code using promise for parallel calls

                    const promise = fileContentItems.map((item) => { return generateTestCasesForAPI({ item: item, testCasesToGenerate: testCasesToGenerate }) })
                    const results = await Promise.allSettled(promise);
                    const allTestCases = [];
                    results.map((result) => {
                        if (result.status === "fulfilled" && result.value?.data?.testcases) {
                            allTestCases.push(...result.value.data.testcases);
                        } else {
                            console.error("One call failed:", result.reason);
                        }
                    })


                    if (allTestCases && allTestCases.length > 0) {
                        const updatedTestCases = allTestCases.map(testcase => {
                            // const isFunctional = testcase.Category?.toLowerCase().includes("functional");
                            const isFunctional = testcase.Category?.toLowerCase() === "functional testing";

                            const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";

                            let prefix = isFunctional ? "TCD_FT" : "TCD_NFT";
                            let shortCode = "";

                            if (subCategory.includes("positive")) shortCode = "POS";
                            else if (subCategory.includes("negative")) shortCode = "NEG";
                            else if (subCategory.includes("boundary")) shortCode = "BOUND";
                            else if (subCategory.includes("error")) shortCode = "ERR";
                            else if (subCategory.includes("security")) shortCode = "SEC";
                            else if (subCategory.includes("performance")) shortCode = "PERF";

                            const padded = String(tcCounter).padStart(2, "0");
                            const id = `${prefix}_${padded}_${shortCode}`;
                            tcCounter++; // increment global counter

                            return {
                                ...testcase,
                                "Test Case ID": id
                            };
                        });

                        finalArray.push(...updatedTestCases);


                    }

                    if (selectedTestCases.includes("Resource Chain test case") || selectionMode === 'all') {
                        progressHandler.updateMessage("Generating Resource Chain Test Cases");
                        const resourceChainResponse = await resourceChainTestCases({ endpoints: fileContentItems });
                        if (resourceChainResponse.data && resourceChainResponse.data.length > 0) {
                            finalArray.push(...resourceChainResponse.data);

                        }
                    }



                } catch (error) {
                    console.error(
                        error?.response?.data?.error || error.message,
                        "Error processing item:"
                    );
                    // setPopupMessage("Error processing some test cases. Please check the file format and try again.");
                    // setPopupOpen(true);
                }
                // const filteredTestCases = finalArray.map(testcase => ({
                //     "Test Case ID": testcase["Test Case ID"],
                //     "API Endpoint": testcase["API Endpoint"],
                //     "Method": testcase["Method"],
                //     "Test Description": testcase["Test Description"],
                //     "Category": testcase["Category"]

                // }))

                // try {
                //     progressHandler.updateMessage("Genertaing Traceability Matrix");
                //     const response = await APITraceabilityMatrix(filteredTestCases);


                //     if (response.data !== null) {
                //         traceabilityMatrix = response.data;
                //         setTraceabilityMatrixData(response.data)

                //     }
                // } catch (error) {
                //     console.error(
                //         error?.response?.data?.error || error.message,
                //         "Error processing item:"

                //     );
                //     setPopupMessage("Error generating traceability matrix. Please try again.");
                //     setPopupOpen(true);
                // }
            }
            else {
                // YAML flow
                try {
                    // Initialize progress bar
                    progressHandler = handleProgressBar({
                        setLoadingState,
                        totalDurationMs: 195000
                    });
                    progressHandler.start("Generating test cases from API endpoints");

                    // Step 1: Convert Swagger to Postman
                    const responseData = await swaggerToPostman(fileContent);

                    if (!responseData?.data?.item || !Array.isArray(responseData.data.item)) {
                        throw new Error("Invalid Swagger structure: Missing 'item' array in response.");
                    }

                    const flattenedItems = flattenPostmanItems(responseData.data.item);

                    // Step 2: Generate test cases for each endpoint
                    const promises = flattenedItems.map(async (item, index) => {
                        try {
                            const testCaseResponse = await generateTestCasesForAPI({
                                item,
                                testCasesToGenerate
                            });

                            if (testCaseResponse?.data?.testcases) {
                                return testCaseResponse.data.testcases;
                            } else {
                                console.warn(`No testcases returned for item ${index}`);
                                return [];
                            }

                        } catch (error) {
                            console.error(
                                `Error processing Swagger item ${index}:`,
                                error?.response?.data?.error || error.message
                            );
                            return []; // Continue without breaking flow
                        }
                    });

                    const results = await Promise.allSettled(promises);

                    // Step 3: Process successful test case results
                    for (const result of results) {
                        if (result.status === "fulfilled" && Array.isArray(result.value)) {
                            const updatedTestCases = result.value.map(testcase => {
                                const category = testcase.Category?.toLowerCase() || "";
                                const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";

                                const isFunctional = category === "functional testing";
                                const prefix = isFunctional ? "TCD_FT" : "TCD_NFT";

                                let shortCode = "GEN";
                                if (subCategory.includes("positive")) shortCode = "POS";
                                else if (subCategory.includes("negative")) shortCode = "NEG";
                                else if (subCategory.includes("boundary")) shortCode = "BOUND";
                                else if (subCategory.includes("error")) shortCode = "ERR";
                                else if (subCategory.includes("security")) shortCode = "SEC";

                                const padded = String(tcCounter).padStart(2, "0");
                                const id = `${prefix}_${padded}_${shortCode}`;
                                tcCounter++;

                                return { ...testcase, "Test Case ID": id };
                            });

                            finalArray.push(...updatedTestCases);
                        }
                    }

                    if (selectedTestCases.includes("Resource Chain test case") || selectionMode === "all") {
                        progressHandler.updateMessage("Generating Resource Chain Test Cases");

                        try {
                            const resourceChainResponse = await resourceChainTestCases({
                                endpoints: fileContent
                            });

                            if (resourceChainResponse?.data?.length) {
                                finalArray.push(...resourceChainResponse.data);
                            }
                        } catch (chainError) {
                            console.error(" Error generating resource chain test cases:", chainError.message);
                        }

                    }

                } catch (error) {
                    console.error("  Error converting swagger to postman:", error.message);
                    setPopupMessage("Error converting swagger to postman. Please try again.");
                    setPopupOpen(true);
                }
            }


            await progressHandler.finishAndClose();
            if (finalArray.length > 0) {
                navigate("/dashboard/api-testing/test-cases-display", {
                    state: {
                        ...location.state,
                        generateTestCasesData: finalArray,
                        button: "importAPIDefinition",
                        // traceabilityMatrix: traceabilityMatrix
                        testCasesToGenerate: testCasesToGenerate

                    },
                });
            } else {
                console.warn("No test cases were generated");
            }
        } catch (error) {
            console.error("Error generating test cases:", error);
            setLoadingState({ open: false, message: "", progress: null });
            setPopupMessage("Something went wrong while generating test cases.");
            setPopupOpen(true);
        } finally {
            if (progressHandler) {
                progressHandler.stop();
            }
            setLoadingState({ open: false, message: "", progress: null });
        }
    };

    const hanldeEndpoitExtraction = async () => {

        let progressHandler;
        progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 216000 });
        progressHandler.start("Generating Test Cases for each end-point");
        if (fileContentItems && fileContentItems.length > 0) {
            try {
                //Mew code using promise for parallel calls
                const promise = fileContentItems.map((item) => { return generateTestCasesForAPI(item) })
                const results = await Promise.allSettled(promise);
                const allTestCases = [];
                results.map((result) => {
                    if (result.status === "fulfilled" && result.value?.data?.testcases) {
                        allTestCases.push(...result.value.data.testcases);
                    } else {
                        console.error("One call failed:", result.reason);
                    }
                })

                if (allTestCases && allTestCases.length > 0) {
                    await progressHandler.finishAndClose();

   
                }
            } catch (error) {
                console.error(error)
            }
        }


    }

    return (
        <Box
            sx={{
                p: 2,
                bgcolor: "#F6F3F2",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                maxHeight: "100%",
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    // height: { xs: "60vh", sm: "65vh", md: "73vh" }, // 

                    overflowY: "scroll", // scrolling works
                    scrollbarWidth: "none", // Firefox
                    "&::-webkit-scrollbar": {
                        display: "none", // Chrome, Safari, Edge
                    },  // adds scroll if content is taller
                    // mx: "auto",
                    mb: 2
                }}
            >
                <Box sx={{ justifyContent: "space-between", display: "flex", gap: 2 }}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            p: 2,
                            flex: 1,
                            mb: 3,
                            border: "none",
                            backgroundColor: "#fff",


                        }}
                    >
                        <Typography variant="Subtitle1" fontWeight="bold" fontSize={17}

                        >Upload Specifications</Typography>
                        {/* Upload Specifications Section */}
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                border: "2px  dashed #0094FB",
                                backgroundColor: "#E1F5FF",
                                textAlign: "center",
                                mb: 2,
                                p: 0.5,
                                mt: 2,
                                cursor: "pointer",
                                transition: "all 0.3s ease",

                                "&.drag-over": {
                                    backgroundColor: "#B8E0FF",
                                    borderColor: "#0066B3",
                                },

                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.add("drag-over");
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove("drag-over");
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove("drag-over");

                                const files = e.dataTransfer.files;
                                if (files && files.length > 0) {
                                    const file = files[0];
                                    // Validate file type
                                    const validExtensions = ['.json', '.yaml', '.yml'];
                                    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

                                    if (validExtensions.includes(fileExtension)) {
                                        // Create a synthetic event object to pass to your existing handler
                                        handleFileUpload({ target: { files: [] } }, file);

                                    } else {
                                        setPopupMessage('Please upload a valid file format: json, yaml, or yml');
                                        setPopupOpen(true);
                                    }
                                }
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, gap: 1 }}>
                                    <UploadIcon />
                                    <Typography variant="subtitle1">
                                        Upload API Specs
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Drag and drop your API definition file here, or click to select

                                    <br />
                                    Format: json, yaml, postman_collection
                                </Typography>

                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{ mt: 2, borderRadius: "8px", backgroundColor: "#0094FB", color: "#fff", textTransform: "none", mb: 0, padding: "4px 16px" }}
                                    onChange={handleFileUpload}
                                >
                                    <WhiteUploadIcon style={{ marginRight: 8 }} />
                                    Choose File
                                    <input hidden accept=".json,.yaml,.yml" type="file" />

                                </Button>
                            </CardContent>
                        </Card>
                    </Card>
                    {uploadedFile && (

                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                p: 2,
                                mb: 3,
                                border: "none",
                                backgroundColor: "#fff",
                                flex: 1


                            }}
                        >

                            <Typography variant="Subtitle1" fontWeight="bold" fontSize={17}>Uploaded File</Typography>

                            <Box sx={{ alignItems: "center", justifyContent: "center", textAlign: "center", mt: 2 }}>


                                <UploadFileIcon />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body1" fontWeight="bold" fontSize={14}>{uploadedFile.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {(uploadedFile.size / 1024).toFixed(1)} KB | {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                </Box>
                                <Button

                                    onClick={() => {
                                        setUploadedFile(null)
                                        setFileContentEndpoints(null);
                                    }
                                    }
                                    sx={{ textTransform: "none", color: "#FF3B30" }}
                                >
                                    <DeleteIcon style={{ marginRight: 4, }} />
                                    Delete
                                </Button>
                            </Box>

                        </Card>
                    )}

                </Box>

                {/* Captured API Endpoints */}
                <Box  >
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            border: "none"
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 30 }} >
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, fontSize: 17 }}>
                                Captured API Endpoints
                            </Typography>
                            <TextField
                                disabled={!fileContentEndpoints}
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon style={{ color: "#a9a9a9", marginRight: 8 }} />
                                    ),
                                }}
                                placeholder="Search by endpoint"
                                variant="outlined"

                                size="small"
                                sx={{ mb: 2, width: 500 }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Box>
                        {/* <Divider sx={{ mb: 2 }} /> */}

                        {/* Empty State */}
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 3,
                                color: "text.secondary",
                                minHeight: 200,
                                overflowX: "auto",
                                overflowY: "auto"

                            }}

                        >
                            {/* <NoDataIcon /> */}

                            {fileContentEndpoints && fileContentEndpoints.length > 0 ? (
                                <TableContainer sx={{ maxHeight: 500 }}>
                                    <Table stickyHeader sx={{ border: '1px solid #e0e0e0', borderLeft: 'none', borderRight: 'none', }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><b>HTTP Method</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>
                                                <TableCell><b>Endpoint</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>
                                                <TableCell><b>Param Type</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>
                                                <TableCell><b>Params</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>
                                                <TableCell><b>Headers</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>
                                                <TableCell><b>Auth Type</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>
                                                <TableCell><b>Body</b>
                                                    <ArrowDownwardOutlinedIcon sx={{
                                                        fontSize: "medium", ml: 0.3, position: "relative",
                                                        top: "3px"
                                                    }} />
                                                </TableCell>

                                            </TableRow>
                                        </TableHead>
                                        <TableBody >
                                            {filteredRows.map((row, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ minWidth: 200 }}>{row.method}</TableCell>
                                                    <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.path}</TableCell>
                                                    <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.paramType || "-"}</TableCell>
                                                    <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.params)}</TableCell>
                                                    <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.headers) || ""}</TableCell>
                                                    <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.authType)}</TableCell>
                                                    <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.body)}</TableCell>

                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box textAlign="center" py={6} color="text.secondary">
                                    <NoDataIcon />
                                </Box>
                            )}


                        </Box>
                    </Card>
                </Box>

            </Box>

            {/* Bottom Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", }}>
                <Button variant="outlined"
                    onClick={() => navigate("/dashboard/api-testing/landingpage")}
                    sx={{
                        borderRadius: "8px", color: "#0094FB", borderColor: " #0094FB",
                        borderWidth: "2px",   // makes border bold
                        fontWeight: "bold", textTransform: "none",
                        padding: "4px 16px"

                    }
                    }
                >
                    <ArrowBackIcon style={{ marginRight: 6, fontSize: 18 }} />
                    Back
                </Button>
                <Button
                    variant="contained"
                    sx={{
                        borderRadius: "8px",
                        backgroundColor: "#0094FB",
                        textTransform: "none",
                        fontWeight: "bold",
                        padding: "4px 16px"
                    }}
                    disabled={!uploadedFile}
                    onClick={handleShowCatergory}
                >
                    Generate test cases
                    <ArrowForwardIcon style={{ marginLeft: 8, fontSize: 18 }} />
                </Button>
            </Box>
            <MessagePopup
                modalOpen={popupOpen}
                message={popupMessage}
                okayHandler={() => setPopupOpen(false)}
            />

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: "40%",
                        bgcolor: '#f8f9fa'
                    }
                }}
            >
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', minHeight: "100vh" }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconButton onClick={handleBack} sx={{ mr: 1, p: 0.5 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                            Test Case Selection
                        </Typography>
                        <IconButton onClick={handleBack} sx={{ p: 0.5 }}>
                            <Close />
                        </IconButton>
                    </Box>

                    {/* Selection Options */}
                    <Box sx={{ mb: 1, flex: 1 }}>
                        <RadioGroup
                            value={selectionMode}
                            onChange={handleSelectionModeChange}
                        >
                            <FormControlLabel
                                value="choose"
                                control={<Radio />}
                                label={
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        Choose test cases to be generated
                                    </Typography>
                                }
                                sx={{ mb: 1 }}
                            />

                            {selectionMode === 'choose' && (
                                <Box sx={{ ml: 4, mb: 1 }}>
                                    {/* Functional Test Cases */}
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 550, fontSize: 14 }}>
                                        Functional Test Cases
                                    </Typography>
                                    <Box sx={{ mb: 2, display: "flex", flexDirection: "column" }}>
                                        {functionalTestCases.map((testCase) => (
                                            <FormControlLabel
                                                key={testCase.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTestCases.includes(testCase.id)}
                                                        onChange={() => handleTestCaseToggle(testCase.id)}
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {testCase.label}
                                                    </Typography>
                                                }
                                            // sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Box>

                                    {/* Non-Functional Test Cases */}
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 550, fontSize: 14 }}>
                                        Non-Functional Test Cases
                                    </Typography>
                                    <Box sx={{ mb: 1, display: "flex", flexDirection: "column" }}>
                                        {nonFunctionalTestCases.map((testCase) => (
                                            <FormControlLabel
                                                key={testCase.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTestCases.includes(testCase.id)}
                                                        onChange={() => handleTestCaseToggle(testCase.id)}
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {testCase.label}
                                                    </Typography>
                                                }

                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label={
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        Generate all test cases
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </Box>

                    {/* Footer Buttons */}
                    <Box sx={{
                        display: "flex", justifyContent: "space-between",
                        gap: 2,
                        p: 2,



                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            sx={{
                                borderRadius: "8px", color: "#0094FB", borderColor: " #0094FB",
                                borderWidth: "2px",   // makes border bold
                                fontWeight: "bold", textTransform: "none",
                                padding: "5px 16px",
                                mb: 1

                            }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={generateTestCases}
                            disabled={selectionMode === 'choose' && selectedTestCases.length === 0}
                            sx={{
                                borderRadius: "8px",
                                backgroundColor: "#0094FB",
                                textTransform: "none",
                                fontWeight: "bold",
                                padding: "5px 16px",
                                mb: 1

                            }}
                        >
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}

export default UploadAPISpecs;
